import { AuthSliceActions, AuthSliceReducer } from "./features/auth/auth.slice";
import {
  layoutSliceActions,
  layoutSliceReducer,
} from "./features/layout/layout.slice";

import {
  combineReducers,
  configureStore,
  isRejectedWithValue,
  Middleware,
} from "@reduxjs/toolkit";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { doNotification } from "../components/toast/nofitication";
import { baseApi } from "./api";

interface ErrorPayload {
  status: number;
  data?: {
    message: string;
    code: number;
    data: object;
  };
}

/**
 * @description Middleware for logging or toast notifications
 * @returns next func
 */
export const rtkQueryResponseFeedbackMiddleware: Middleware =
  () => (next) => (action) => {
    const dispatch = store.dispatch;

    if (isRejectedWithValue(action)) {
      const errorPayload = action.payload as ErrorPayload;

      if (
        errorPayload?.status === 401 &&
        errorPayload?.data?.message?.toLowerCase() === "session expired"
      ) {
        doNotification("Session expired, please login", "warning");

        dispatch(layoutSliceActions.reset());
        dispatch(AuthSliceActions.logout());
      }
    }

    return next(action);
  };

const rootReducer = combineReducers({
  [baseApi.reducerPath]: baseApi.reducer,
  layout: layoutSliceReducer,
  auth: AuthSliceReducer,
});

const persistConfig = {
  key: "root",
  storage,
  blacklist: [baseApi.reducerPath],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat([baseApi.middleware, rtkQueryResponseFeedbackMiddleware]);
  },
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
